/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
import { t } from "i18next";
import lod_ from "lodash";
import { Divider, Icon, InputAdornment } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDInput from "components/Basics/MDInput";
import ButtonMenu from "components/Custom/ButtonMenu";
import DateFilter from "components/Custom/Filters/DateFilter";
import { getLabel } from "helpers/dictionary";
import { useDispatch, useSelector, useStore } from "react-redux";
import { deleteFilter, setFilter } from "redux-react/reducers/filtersReducers";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";
import InputString from "./filters/InputString";
import {
	getLocalStorageFrontValues,
	getLocalStorageBackValues
} from "components/Custom/Filters/filters";
import { useEffect, useMemo, useState } from "react";
import ChartsActions from "redux-react/actions/chartsActions";
import { getInterval } from "components/Custom/DatePresets";

// Stable reference while the definitions are not loaded
const EMPTY_DEFINITIONS = [];

/**
 * Build the page filters sent to the back
 * Keep only the stored filters that are defined on the page
 * @param {[object]} pageFilterDefs - filters defined on the page
 * @param {object} storedFilters - back values stored for the page
 * @return {object} filters by attribute
 */
export function buildPageFilters(pageFilterDefs, storedFilters) {
	const attributes = pageFilterDefs.map(pageFilter => pageFilter.attribute);
	return lod_.pickBy(storedFilters, filter => attributes.includes(filter?.name));
}

/**
 * Parse filters defined by string into
 * an component's array
 * @param {[String]} filters
 */
function useFilters(route) {
	const page = route.route;

	const profile = useSelector(selectCurrentProfile);
	const language = profile?.language || "fr";
	const assistantID = profile?.assistantID;
	const dispatch = useDispatch();
	// Used to read the up-to-date store right after dispatching (async init)
	const store = useStore();

	// Filters defined on the page, fetched from backend: { assistantID, page, list }
	const [pageFilterDefs, setPageFilterDefs] = useState(null);
	const [component, setComponent] = useState([]);
	// Store filters
	const storeFilters = useSelector(state => state.filters);

	/**
	 * Get page filters from backend
	 * @return {Promise} filters - page filters
	 */
	const getPageFilters = () => {
		return new Promise(resolve => {
			dispatch(
				ChartsActions.getPageFilters(assistantID, route, res => {
					resolve(res.filters ?? []);
				})
			);
		});
	};

	/**
	 * Init the store for the page, once after fetching the page filters:
	 * - remove stored filters that are not on the page
	 * - apply defaultValue from page filter config when no user value is stored
	 * @param {[object]} pageFilterDefs - filters defined on the page
	 */
	const initStoreFilters = pageFilterDefs => {
		const attributes = pageFilterDefs.map(pageFilter => pageFilter.attribute);
		const storedFilters = getLocalStorageBackValues(assistantID, page, store.getState().filters);

		// Remove unused filters on page, we can have filters in local storage that are not on the page
		for (let attribute of Object.keys(storedFilters)) {
			if (!attributes.includes(storedFilters[attribute]?.name)) {
				dispatch(deleteFilter({ assistantID, page, attribute }));
			}
		}

		for (let pageFilter of pageFilterDefs) {
			if (lod_.isNil(pageFilter.defaultValue)) {
				continue;
			}
			// Only apply if no user value is already stored for this filter
			let existingFilter = storedFilters[pageFilter.attribute];
			let hasExistingValue =
				!lod_.isNil(existingFilter) &&
				(!lod_.isEmpty(existingFilter?.values) || !lod_.isNil(existingFilter?.value));

			if (hasExistingValue === true) {
				continue;
			}
			// Apply default date preset
			if (pageFilter.type === "datetime" || pageFilter.type === "date") {
				const interval = getInterval(pageFilter.defaultValue);
				if (lod_.isNil(interval) || lod_.isEmpty(interval)) {
					continue;
				}
				dispatch(
					setFilter({
						version: "2.0",
						assistantID,
						page,
						attribute: pageFilter.attribute,
						type: "datetime",
						mode: "auto",
						code: pageFilter.defaultValue,
						values: interval
					})
				);
			}
		}
	};

	/**
	 * Set filter to store
	 * @param {string} assistantID - assistant identifier
	 * @param {string} page - page identifier
	 * @param {object} filter - filter object
	 * @param {any} front - front value
	 * @param {any} back - back value
	 */
	function setFilterToStore(assistantID, page, filter, front, back) {
		dispatch(
			setFilter({
				assistantID,
				page,
				attribute: filter.attribute,
				front,
				back: {
					name: filter.attribute,
					value: back,
					type: filter.type,
					dictionaryPath: filter.dictionaryPath,
					links: filter.links
				}
			})
		);
	}

	/**
	 * Update store filter
	 * @param {any} value - value to set
	 * @param {object} filter - filter object
	 * @param {string} page - page identifier
	 */
	const updateStoreFilter = (value, filter, page) => {
		if (lod_.isNil(value) || lod_.isEmpty(value)) {
			dispatch(
				deleteFilter({
					assistantID,
					page,
					attribute: filter.attribute
				})
			);
		} else {
			const mongoRegex = { $in: value };
			// const mongoRegex = { $regex: value, $options: "i" };
			setFilterToStore(assistantID, page, filter, value, mongoRegex);
		}
	};

	/**
	 * Debounced version of updateStoreFilter
	 */
	const updateStoreFilterDebounce = lod_.debounce((value, filter, page) => {
		if (!value.trim()) {
			dispatch(
				deleteFilter({
					assistantID,
					page,
					attribute: filter.attribute
				})
			);
		} else {
			const mongoRegex = { $regex: value, $options: "i" };
			setFilterToStore(assistantID, page, filter, value, mongoRegex);
		}
	}, 300);

	/**
	 * Create component filters
	 * @param {[object]} loadedPageFilters - filters defined on the page
	 * @param {object} filters - store filters, used for the components initial values
	 * @return {[object]} components
	 */
	const createComponentFilters = (loadedPageFilters, filters) => {
		let newComponentFilters = [];

		for (let filter of loadedPageFilters) {
			switch (filter.type) {
				case "boolean":
					newComponentFilters.push(
						<MDBox key={filter.attribute} mr={1}>
							<ButtonMenu
								fullHeight
								title={getLabel(filter.label, language)}
								type="boolean"
								inverted={filter.inverted}
								optionnal={filter.optionnal}
								storage={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
								handleUpdate={items => {
									if (lod_.isEmpty(items)) {
										let filterObject = {
											assistantID,
											page,
											attribute: filter.attribute
										};

										dispatch(deleteFilter(filterObject));
									} else {
										let value = lod_.isObject(items.value) ? items.value : { $in: [items.value] };
										setFilterToStore(assistantID, page, filter, items, value);
									}
								}}
							/>
						</MDBox>
					);
					break;
				case "datetime":
				case "date":
					newComponentFilters.push(
						<MDBox mr={1} key={filter.attribute} display="flex">
							<DateFilter
								assistantID={assistantID}
								page={page}
								storage={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
								tooltip
								attribute={filter.attribute}
								title={
									getLabel(filter.label, language) ||
									filter.label ||
									t(`FILTERS.${filter.attribute}`)
								}
							/>
						</MDBox>
					);
					break;
				case "code":
					newComponentFilters.push(
						<MDBox key={filter.dictionaryPath} mr={1}>
							<ButtonMenu
								fullHeight
								title={getLabel(filter.label, language)}
								items={filter.values}
								storage={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
								handleUpdate={items => {
									if (!items.length) {
										let filterObject = {
											assistantID,
											page,
											attribute: filter.attribute
										};

										dispatch(deleteFilter(filterObject));
									} else {
										let values = items.map(i => i.value);
										setFilterToStore(assistantID, page, filter, items, { $in: values });
									}
								}}
							/>
						</MDBox>
					);
					break;
				default:
					if (filter.type === "string") {
						newComponentFilters.push(
							<InputString
								storage={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
								filter={filter}
								page={page}
								language={language}
								updateStoreFilterDebounce={updateStoreFilterDebounce}
								updateStoreFilter={updateStoreFilter}
							/>
						);
					} else {
						newComponentFilters.push(
							<MDBox key={filter.attribute} mr={1}>
								<MDInput
									label={filter.label.fr}
									type={filter.type}
									value={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
									storage={getLocalStorageFrontValues(assistantID, page, filter.attribute, filters)}
									onChange={e => {
										if (lod_.isNil(e.target.value) || lod_.isEmpty(e.target.value)) {
											dispatch(
												deleteFilter({
													assistantID,
													page,
													attribute: filter.attribute
												})
											);
										} else {
											setFilterToStore(assistantID, page, filter, e.target.value, e.target.value);
										}
									}}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start" sx={{ ml: 1, mr: 0 }}>
												<Icon size="small">filter_alt</Icon>
											</InputAdornment>
										)
									}}
									placeholder={`Filtrer ${getLabel(filter.label, language)} ...`}
								/>
							</MDBox>
						);
					}
					break;
			}
		}

		if (!lod_.isEmpty(newComponentFilters)) {
			newComponentFilters.unshift(
				<Divider
					variant="vertical"
					sx={{
						marginLeft: 1,
						width: "2px",
						minHeight: "5vh",
						height: "100%",
						color: "#ccc",
						opacity: "0.6",
						backgroundImage:
							"linear-gradient(to right, rgba(52, 71, 103, 0), rgba(52, 71, 103, 0.4), rgba(52, 71, 103, 0)) !important"
					}}
				/>
			);
		}

		return newComponentFilters;
	};

	/**
	 * Load the page filters when route or assistantID changes
	 */
	useEffect(() => {
		// Ignore the response if the page changed meanwhile
		let cancelled = false;

		setPageFilterDefs(null);
		setComponent([]);

		(async () => {
			const list = await getPageFilters();
			if (cancelled) {
				return;
			}
			initStoreFilters(list);
			setComponent(createComponentFilters(list, store.getState().filters));
			setPageFilterDefs({ assistantID, page, list });
		})();

		return () => {
			cancelled = true;
		};
	}, [page, assistantID]);

	// Ready once the page filters of the current page are loaded
	const ready = pageFilterDefs?.page === page && pageFilterDefs?.assistantID === assistantID;

	// Page filters sent to the back, derived from the store (local storage cache + defaults)
	const filters = useMemo(() => {
		if (!ready) {
			return null;
		}
		return buildPageFilters(
			pageFilterDefs.list,
			getLocalStorageBackValues(assistantID, page, storeFilters)
		);
	}, [ready, pageFilterDefs, storeFilters, assistantID, page]);

	return {
		component,
		ready,
		// null until ready, so partially built filters can't be used
		filters,
		// Page filters definitions (labels, values...), empty until ready
		definitions: ready ? pageFilterDefs.list : EMPTY_DEFINITIONS
	};
}

export default useFilters;
